import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import EventCard, { describe, kindColor, type FeedItem } from "../components/EventCard";
import { fmtD, fmtT } from "../utils/period";

type KindFilter = "all" | "trip" | "stops" | "dip" | "alarm" | "case";
const KIND_QUERY: Record<KindFilter, string> = { all: "", trip: "trip", stops: "stop,start", dip: "dip", alarm: "alarm", case: "case" };

// «События → Лента»: every source in one list, newest first, with a kind and
// a generator filter; «Показать ещё» pages backwards in time.
export default function EventsFeed({ hasUnits, hasAlarms, hasCases, onOpenCase }: {
  hasUnits: boolean; hasAlarms: boolean; hasCases: boolean; onOpenCase: (key: string) => void;
}) {
  const { t } = useT();
  const [kind, setKind] = useState<KindFilter>("all");
  const [unit, setUnit] = useState<0 | 1 | 2>(0);
  const [items, setItems] = useState<FeedItem[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sel, setSel] = useState<FeedItem | null>(null);
  const [more, setMore] = useState(false);

  const load = (before?: string | null) => {
    const p = new URLSearchParams({ limit: "30" });
    if (KIND_QUERY[kind]) p.set("kinds", KIND_QUERY[kind]);
    if (unit) p.set("unit", String(unit));
    if (before) p.set("before", before);
    return api.get<{ items: FeedItem[]; next_before: string | null }>(`/api/events?${p.toString()}`);
  };
  useEffect(() => {
    setItems(null); setErr(null);
    load().then(r => { setItems(r.items); setNext(r.next_before); }).catch((e: ApiError) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, unit]);
  const loadMore = async () => {
    if (!next) return;
    setMore(true);
    try { const r = await load(next); setItems(x => [...(x ?? []), ...r.items]); setNext(r.next_before); }
    catch (e) { setErr((e as ApiError).message); }
    finally { setMore(false); }
  };

  const kinds: { k: KindFilter; label: string }[] = [
    { k: "all", label: t("events.k_all") },
    ...(hasUnits ? [{ k: "trip" as KindFilter, label: t("events.k_trip") }, { k: "dip" as KindFilter, label: t("events.k_dip") }, { k: "stops" as KindFilter, label: t("events.k_stops") }] : []),
    ...(hasAlarms ? [{ k: "alarm" as KindFilter, label: t("events.k_alarm") }] : []),
    ...(hasCases ? [{ k: "case" as KindFilter, label: t("events.k_case") }] : []),
  ];
  const chip = (active: boolean, label: string, onClick: () => void) => (
    <button key={label} className={"turbine-chip" + (active ? " active" : "")} style={{ padding: "6px 11px", fontSize: 13 }}
      onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); onClick(); }}>
      <span className="turbine-chip-name">{label}</span>
    </button>
  );
  let lastDay = "";
  return (
    <div>
      <div className="turbine-switcher" style={{ marginBottom: 6 }}>{kinds.map(k => chip(kind === k.k, k.label, () => setKind(k.k)))}</div>
      {hasUnits && (
        <div className="turbine-switcher" style={{ marginBottom: 10 }}>
          {([[0, t("events.u_all")], [1, "G1"], [2, "G2"]] as const).map(([u, l]) => chip(unit === u, l, () => setUnit(u)))}
        </div>
      )}
      {err && <div className="alert error">{err}</div>}
      {!items && !err && <div className="empty-state"><span className="spinner" /></div>}
      {items && items.length === 0 && <div className="empty-state">{t("events.none")}</div>}
      {items && items.length > 0 && (
        <div className="card">
          {items.map((it, i) => {
            const day = fmtD(it.ts);
            const showDay = day !== lastDay; lastDay = day;
            const dsc = describe(it, t);
            return (
              <div key={`${it.kind}-${it.id}`}>
                {showDay && <div style={{ fontSize: 11, color: "var(--hint)", textTransform: "uppercase", letterSpacing: ".05em", margin: i ? "10px 0 2px" : "0 0 2px" }}>{day}</div>}
                <div onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); setSel(it); }}
                  style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 8, alignItems: "center", padding: "7px 0", borderTop: "1px solid var(--border)", cursor: "pointer", fontSize: 13 }}>
                  <i style={{ width: 8, height: 8, borderRadius: "50%", background: kindColor(it.kind) }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{dsc.title}</div>
                    {dsc.sub && <div style={{ fontSize: 11, color: "var(--hint)" }}>{dsc.sub}</div>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--hint)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmtT(it.ts)}</div>
                </div>
              </div>
            );
          })}
          {next && <button className="btn secondary" style={{ marginTop: 10 }} disabled={more} onClick={loadMore}>{more ? <span className="spinner" /> : t("alarms.load_more")}</button>}
        </div>
      )}
      {sel && <EventCard item={sel} onClose={() => setSel(null)} onOpenCase={onOpenCase} />}
    </div>
  );
}
